import { useCallback, useMemo } from 'react';

import {
  XAxis as RechartsXAxis,
  XAxisProps as RechartsXAxisProps,
} from 'recharts';

import { XAxisProps, XAxisTickProps } from './types';
import { XAxisTick } from './XAxisTick';
import { AxisTickConfig } from '../../_types';
import { useChartContext } from '../../ChartProvider';
import { tokens } from '../../../../theme/generated/tokens';

const defaultXAxisProps = {
  minTickGap: 8,
  tickMargin: 8,
  allowDecimals: false,
  allowDuplicatedCategory: false,
  axisLine: false,
  tickLine: false,
} satisfies Partial<XAxisProps<unknown>>;

const defaultTickConfig: AxisTickConfig = {
  backgroundColor: 'transparent',
  color: tokens.theme.fg.structural.secondary,
  paddingX: 2,
  paddingY: 2,
  fontSize: 10,
};

export const XAxis = <T, V = string | number>({
  dataKey,
  tick,
  ...rest
}: XAxisProps<T, V>) => {
  const { chartBlocks } = useChartContext();
  const finalProps = { ...defaultXAxisProps, ...rest };

  const tickConfig = useMemo(() => {
    return typeof tick === 'object'
      ? { ...defaultTickConfig, ...tick }
      : defaultTickConfig;
  }, [tick]);

  const renderTick = useCallback(
    (props: XAxisTickProps<V>) => {
      return <XAxisTick {...props} tickConfig={tickConfig} />;
    },
    [tickConfig]
  );

  return (
    <RechartsXAxis
      className="x-axis-container"
      padding={chartBlocks.bar > 0 ? 'no-gap' : { left: 20, right: 20 }}
      dataKey={dataKey as RechartsXAxisProps<T, V>['dataKey']}
      {...finalProps}
      tick={typeof tick === 'function' ? tick : renderTick}
      mirror={false}
    />
  );
};
